import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import {
  actions,
  isOwner,
  taskStatusByCode,
  projectStatusConfig,
  taskStatusesConfig,
  isProjectFullBlocked,
} from 'entities';

import { EntityStatusTag } from 'components/Entities';
import { StatusSelect } from 'pages/Projects/components';

import { useToast } from 'providers/Toast';
import { useJustification } from 'providers/Justification';
import { useTaskModal } from '../context.taskModal';
import { useUser } from 'hooks';

import { MESSAGES } from '../utils';

export function TaskStatus() {
  const { t } = useLang();
  const { addToast } = useToast();
  const loggedUser = useUser();

  const { task, updateTask, project } = useTaskModal();

  const { openJustificationModal } = useJustification();

  const statusConfig = taskStatusByCode[task?.status];

  const changeStatus = actions.projects.changeTaskStatus.use({
    onSuccess: (_, { status }) => updateTask({ status }),
  });

  const executeStatusChange = useCallback(
    async (status: string, justification?: string) => {
      await changeStatus.execute({
        task: task.id,
        project: project.id,
        status,
        justification,
      });
    },
    [task.id, project.id, changeStatus],
  );

  const handleStatusChange = useCallback(
    async (status: string) => {
      if (status === task.status) return;

      if (status === taskStatusesConfig.notStated.code && task.subTasks.length)
        return addToast(MESSAGES.existingSubTasks(t));

      const doneCode = taskStatusesConfig.done.code;

      const isDoneChange = status === doneCode;

      if (isDoneChange && task.subTasks.some((sub) => sub.status !== doneCode))
        return addToast(MESSAGES.taskDoneWithoutSubsDone(t));

      const additionalInfoNeeded =
        isDoneChange &&
        [task.requireExcludedOWIds, task.requireSampleProjectIds].some(Boolean) &&
        !task.canFinish;

      if (additionalInfoNeeded) return addToast(MESSAGES.additionalInfo(t));

      const toStatusConfig = taskStatusByCode[status];

      const textGetter = toStatusConfig.changes?.getTo || statusConfig.changes?.getFrom;

      if (!textGetter) return executeStatusChange(status);

      openJustificationModal({
        text: textGetter(t),
        cancel: () => addToast(MESSAGES.statusChangeAborted(t)),
        onConfirm: (justification) => executeStatusChange(status, justification),
      });
    },
    [addToast, task, executeStatusChange, statusConfig, openJustificationModal, t],
  );

  const canDisplayStatusSelect = [
    isOwner(task, loggedUser),
    !isProjectFullBlocked(project.status),
    !statusConfig.blocks.fullBlock,
    statusConfig.canChangeStatus,

    ![projectStatusConfig.done.code, projectStatusConfig.canceled.code].some(
      (code) => code === project.status,
    ),
  ].every(Boolean);

  return canDisplayStatusSelect ? (
    <StatusSelect
      value={task.status}
      onChange={handleStatusChange}
      statusConfig={taskStatusesConfig}
      loading={changeStatus.loading}
      className="min-h-0 py-0.5 text-sm [&_svg]:size-3"
    />
  ) : (
    <EntityStatusTag target="task" status={task.status} round={false} size="medium" />
  );
}
